import React, { useCallback, useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller, useFieldArray } from 'react-hook-form';
import CreatableSelect from 'react-select/creatable';
import * as zod from 'zod';
import type {
  SpotForm_ChartererFragment,
  VoyageCharterForm_AssortmentFragment,
  VoyageCharterForm_CoaCommissionFragment,
  VoyageCharterForm_CoaRouteFragment,
} from '../../graphql';
import { CommissionType, useCreateOrganizationMutation } from '../../graphql';
import { nodesToOptions, reactSelectStyles } from '../../lib/form-util';
import {
  valueOrNull,
  reactSelectOptionSchema,
  stringToNumber,
} from '../../lib/zod-util';
import { useCurrentUser } from '../../state/zustand';
import {
  Box,
  Button,
  FormSection,
  Input,
  Select,
  Table,
  TableCell,
  TableHeader,
  TableHeaders,
  TableRow,
} from '../ui';
import { commissionSchema } from './schema';
import type { SpotFormQueryRefetch } from './SpotForm';
import type {
  RelatedOrderSchema,
  VoyageCharterFormSchema,
} from './VoyageCharterForm';

const commissionTypeOptions = [
  {
    value: CommissionType.LUMPSUM,
    label: 'Lumpsum',
  },
  {
    value: CommissionType.PERCENTAGE,
    label: 'Percentage of freight',
  },
  {
    value: CommissionType.RATE,
    label: 'Rate per unit',
  },
];

export const commissionFieldsSchema = zod.object({
  commissions: zod.array(
    commissionSchema.extend({
      organizationId: reactSelectOptionSchema,
    }),
  ),
});

const CHECK_BOX_SIZE = {
  width: 20,
  height: 20,
};

type CommissionFieldsSchema = zod.infer<typeof commissionFieldsSchema>;

function findMatchingRelatedOrder(
  coaCommission: VoyageCharterForm_CoaCommissionFragment,
  relatedOrders: RelatedOrderSchema[],
): boolean {
  return relatedOrders.some(
    ({ coaRouteId, order }) =>
      (coaCommission.coaRouteId === coaRouteId?.value &&
        coaCommission.coaContractAssortmentByCoaAssortmentId?.assortmentId ===
          order.assortmentId) ||
      (coaCommission.coaAssortmentId === null &&
        coaCommission.coaRouteId === null) ||
      (coaCommission.coaAssortmentId === null &&
        coaCommission.coaRouteId === coaRouteId?.value) ||
      (coaCommission.coaContractAssortmentByCoaAssortmentId?.assortmentId ===
        order.assortmentId &&
        coaCommission.coaRouteId === null),
  );
}

function findMatchingDefaultCommission(
  coaCommission: VoyageCharterForm_CoaCommissionFragment,
  defaultCommissions: CommissionFieldsSchema['commissions'],
): boolean {
  return defaultCommissions.some(
    ({
      organizationId,
      assortmentId,
      coaRouteId,
      loadPortId,
      dischargePortId,
    }) => {
      const assortmentIdByCoa =
        coaCommission.coaContractAssortmentByCoaAssortmentId?.assortmentId ??
        null;
      return (
        (assortmentIdByCoa === assortmentId ||
          (assortmentId === 0 && coaCommission.coaAssortmentId === null)) &&
        (coaCommission.coaRouteId === coaRouteId ||
          loadPortId === null ||
          dischargePortId === null) &&
        coaCommission.commissionByCommissionId?.organizationId ===
          organizationId?.value
      );
    },
  );
}

function getCoaCommission(
  relatedOrders: RelatedOrderSchema[],
  coaCommissions: VoyageCharterForm_CoaCommissionFragment[],
  defaultCommissions: CommissionFieldsSchema['commissions'],
) {
  return coaCommissions.filter(
    (Cm) =>
      findMatchingRelatedOrder(Cm, relatedOrders) &&
      !findMatchingDefaultCommission(Cm, defaultCommissions),
  );
}

export default function CommissionFields<T extends CommissionFieldsSchema>({
  form,
  refetch,
  organizations,
  assortments,
  defaultCommissions,
  coaCommissions,
  coaRoutes,
  enableCoaRouteSelect,
}: {
  form: UseFormReturn<T>;
  refetch: SpotFormQueryRefetch;
  organizations: SpotForm_ChartererFragment[];
  assortments: VoyageCharterForm_AssortmentFragment[];
  defaultCommissions: CommissionFieldsSchema['commissions'];
  coaCommissions: VoyageCharterForm_CoaCommissionFragment[];
  coaRoutes: VoyageCharterForm_CoaRouteFragment[];
  enableCoaRouteSelect: boolean;
}): JSX.Element {
  const [createOrganization, { loading: createOrganizationLoading }] =
    useCreateOrganizationMutation();
  const organisationId = useCurrentUser()?.organizationId ?? null;

  const { control, setValue, register } =
    form as UseFormReturn<CommissionFieldsSchema>;

  const { fields, append, remove } = useFieldArray<
    CommissionFieldsSchema,
    'commissions',
    'key'
  >({
    control,
    name: 'commissions',
    keyName: 'key',
  });

  const { watch } = form as unknown as UseFormReturn<VoyageCharterFormSchema>;

  const relatedOrders = watch('relatedOrders');

  useEffect(() => {
    const formatedDefaultCommissions = defaultCommissions.map((Cm) => ({
      ...Cm,
      coaRouteId:
        coaRoutes.find(
          ({ originId, destinationId }) =>
            Cm.loadPortId === originId && Cm.dischargePortId === destinationId,
        )?.id ?? 0,
      assortmentId: Cm.assortmentId ?? 0,
    }));

    const coaCommissionsByRelatedOrders = getCoaCommission(
      relatedOrders,
      coaCommissions,
      formatedDefaultCommissions,
    );

    setValue('commissions', [
      ...formatedDefaultCommissions,
      ...coaCommissionsByRelatedOrders.map((Cm) => ({
        type: Cm.commissionByCommissionId?.type ?? CommissionType.LUMPSUM,
        value: Cm.commissionByCommissionId?.value
          ? +Cm.commissionByCommissionId?.value
          : 0,
        id: null,
        assortmentId: Cm.coaAssortmentId
          ? Cm.coaContractAssortmentByCoaAssortmentId?.assortmentId
            ? +Cm.coaContractAssortmentByCoaAssortmentId?.assortmentId
            : 0
          : 0,
        coaRouteId: Cm.coaRouteId ?? 0,
        loadPortId: Cm.coaContractPortByCoaRouteId?.originId ?? null,
        dischargePortId: Cm.coaContractPortByCoaRouteId?.destinationId ?? null,
        organizationId: {
          value: Cm.commissionByCommissionId?.organizationId ?? 0,
          label:
            Cm.commissionByCommissionId?.organizationByOrganizationId?.name ??
            '',
        },
        demurrageApplied:
          Cm.commissionByCommissionId?.demurrageApplied ?? false,
        extraFreightApplied:
          Cm.commissionByCommissionId?.extraFreightApplied ?? false,
        bafApplied: Cm.commissionByCommissionId?.bafApplied ?? false,
      })),
    ]);
  }, [coaCommissions, coaRoutes, defaultCommissions, relatedOrders, setValue]);

  const organizationOptions = useMemo(
    () => nodesToOptions(organizations),
    [organizations],
  );

  const assortmentOptions = useMemo(() => {
    const options = assortments.map((assortment) => ({
      value: assortment.id,
      label: assortment.name,
    }));

    options.push({
      value: 0,
      label: 'Apply to all commodities',
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [assortments]);

  const coaRouteOptions = useMemo(() => {
    const formatedCoaRoutes = coaRoutes.map((coaRoute) => ({
      value: coaRoute.id,
      label: `${coaRoute.locationByOriginId?.name} → ${coaRoute.locationByDestinationId?.name}`,
      loadPortId: coaRoute.originId,
      loadPortName: coaRoute.locationByOriginId?.name ?? 'Load port name N/A',
      dischargePortId: coaRoute.destinationId,
      dischargePortName:
        coaRoute.locationByDestinationId?.name ?? 'Discharge port name N/A',
    }));
    formatedCoaRoutes.push({
      value: 0,
      label: 'Apply to all ports',
      loadPortId: 0,
      loadPortName: '',
      dischargePortId: 0,
      dischargePortName: '',
    });
    return formatedCoaRoutes.sort((a, b) => a.label.localeCompare(b.label));
  }, [coaRoutes]);

  const handleCreateOrganization = useCallback(
    async (inputValue: string, idx: number) => {
      const { data } = await createOrganization({
        variables: {
          input: {
            organization: {
              name: inputValue,
              parentOrganizationId: organisationId,
            },
          },
        },
      });

      const { id, name } = { ...data?.createOrganization?.organization };
      setValue(`commissions.${idx}.organizationId`, {
        value: id ?? 0,
        label: name ?? '',
      });
      await refetch();
    },
    [createOrganization, refetch, organisationId, setValue],
  );

  return (
    <FormSection title="Commissions" display="block" visibilityId="commissions">
      <Box
        borderWidth={1}
        borderStyle="solid"
        borderColor="neutral.gray_225"
        overflow="visible"
        mb={3}
      >
        <Table>
          <thead>
            <TableHeaders>
              <TableHeader width={200}>Type</TableHeader>
              <TableHeader width={200}>Value</TableHeader>
              <TableHeader width={200}>Organization</TableHeader>
              {enableCoaRouteSelect && (
                <>
                  <TableHeader width={200}>Commodity</TableHeader>
                  <TableHeader width={200}>COA Route</TableHeader>
                </>
              )}

              <TableHeader width={200}>Apply on BAF</TableHeader>
              <TableHeader width={200}>Apply on extra freight</TableHeader>
              <TableHeader width={200}>Apply on demurrage</TableHeader>
              <TableHeader width={100}>Actions</TableHeader>
            </TableHeaders>
          </thead>
          <tbody>
            {fields.map((f, i) => {
              return (
                <TableRow key={f.key}>
                  <TableCell>
                    <Select
                      {...register(`commissions.${i}.type`, {
                        setValueAs: valueOrNull,
                      })}
                      options={commissionTypeOptions}
                      placeholder="-"
                      enablePlaceholder
                      required
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      required
                      type="number"
                      step="any"
                      {...register(`commissions.${i}.value`, {
                        setValueAs: stringToNumber,
                      })}
                    />
                  </TableCell>
                  <TableCell data-testid={`commission-organization`}>
                    <Controller
                      name={`commissions.${i}.organizationId`}
                      control={control}
                      render={({ field }) => {
                        return (
                          <CreatableSelect
                            isLoading={createOrganizationLoading}
                            isDisabled={createOrganizationLoading}
                            styles={reactSelectStyles}
                            options={organizationOptions}
                            onCreateOption={(inputValue) =>
                              handleCreateOrganization(inputValue, i)
                            }
                            {...field}
                          />
                        );
                      }}
                    />
                  </TableCell>
                  {enableCoaRouteSelect && (
                    <>
                      <TableCell>
                        <Select
                          {...register(`commissions.${i}.assortmentId`, {
                            setValueAs: stringToNumber,
                          })}
                          options={assortmentOptions}
                          data-testid={`commission-${i}-assortment`}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          {...register(`commissions.${i}.coaRouteId`, {
                            setValueAs: stringToNumber,
                          })}
                          options={coaRouteOptions}
                          data-testid={`commission-${i}-coa-route`}
                          onChange={({ target }) => {
                            const targetCoaRoute = coaRouteOptions.find(
                              ({ value }) => value === +target.value,
                            );
                            setValue(
                              `commissions.${i}.loadPortId`,
                              targetCoaRoute?.loadPortId ?? null,
                            );
                            setValue(
                              `commissions.${i}.dischargePortId`,
                              targetCoaRoute?.dischargePortId ?? null,
                            );
                          }}
                        />
                      </TableCell>
                    </>
                  )}
                  <TableCell>
                    <Input
                      type="checkbox"
                      {...register(`commissions.${i}.bafApplied`)}
                      style={CHECK_BOX_SIZE}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="checkbox"
                      {...register(`commissions.${i}.extraFreightApplied`)}
                      style={CHECK_BOX_SIZE}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="checkbox"
                      {...register(`commissions.${i}.demurrageApplied`)}
                      style={CHECK_BOX_SIZE}
                    />
                  </TableCell>
                  <TableCell>
                    <Button variant="secondary" onPress={() => remove(i)}>
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </Box>
      <Button
        variant="secondary"
        onPress={() =>
          append({
            type: CommissionType.LUMPSUM,
            value: 0,
            id: null,
            organizationId: {
              value: 0,
              label: '',
            },
            demurrageApplied: null,
            extraFreightApplied: null,
            bafApplied: null,
            assortmentId: null,
            loadPortId: null,
            dischargePortId: null,
            coaRouteId: null,
          })
        }
      >
        Add commission item
      </Button>
    </FormSection>
  );
}
